import React, { CSSProperties } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
import {
  FaCoffee,
  FaStar,
  FaStore,
  FaMapMarkerAlt,
  FaListAlt,
  FaUser,
  FaCog,
  FaSearch,
} from 'react-icons/fa';
import { useNotifications } from '../context/NotificationContext';

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { unreadCount, showPanel, setShowPanel } = useNotifications();
  const isNative = Capacitor.isNativePlatform();
  const darkModeEnabled =
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  const navStyle: CSSProperties = darkModeEnabled
    ? {
        paddingTop: 'env(safe-area-inset-top)',
        backgroundImage: isNative
          ? "url('/header-coffee.webp')"
          : "url('/header-coffee-website.webp')",
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        minHeight: isNative ? 72 : 140,
        backgroundPosition: isNative ? 'top center' : 'center',
      }
    : {
        paddingTop: 'env(safe-area-inset-top)',
        backgroundImage: 'linear-gradient(135deg, #2f1f15 0%, #5a3b29 55%, #8c6345 100%)',
        minHeight: isNative ? 72 : 110,
      };

  const buttonBgClass = darkModeEnabled
    ? 'bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white'
    : 'bg-[#b08f76] hover:bg-[#c09c81] text-white';
  const onCafesPage = location.pathname === '/cafes' || location.pathname === '/';
  const cafeSearch = searchParams.get('q') || '';

  const updateCafeSearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value.trim()) {
      next.set('q', value);
    } else {
      next.delete('q');
    }
    setSearchParams(next, { replace: true });
  };

  if (!user) {
    return null;
  }

  return (
    <nav className="shadow-lg text-white" style={navStyle}>
      <div className="container mx-auto px-4 py-3 pt-6">
        {/* Top Row: Logo and Main Actions */}
        <div className="flex items-center justify-between mb-2">
          <Link
            to="/cafes"
            className="text-xl font-bold tracking-wide flex items-center gap-2 hover:opacity-90 transition"
            aria-label="qahwahub home"
          >
            <FaCoffee className="text-2xl shrink-0" aria-hidden />
            <span>qahwahub</span>
          </Link>
          
          <div className="flex items-center gap-1.5">
            {!user.isGuest ? (
              <Link to="/profile" className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-200 shadow-md hover:shadow-lg ${darkModeEnabled ? 'bg-gradient-to-r from-amber-700 to-amber-600 hover:from-amber-600 hover:to-amber-500' : 'bg-[#6b432b] text-[#fff9f2]'}`}>
                <span className="text-sm font-medium">
                  {user.points.toString().replace(/\.0+$/, '')} pts
                </span>
                {user.subscription_active ? (
                  <span className="bg-gradient-to-r from-yellow-400 to-yellow-300 text-gray-800 px-1.5 py-0.5 rounded text-xs font-bold shadow-sm">
                    <FaStar className="inline" />
                  </span>
                ) : null}
              </Link>
            ) : (
              <Link
                to="/login"
                onClick={() => logout()}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg ${buttonBgClass}`}
              >
                Sign In
              </Link>
            )}
            <button
              type="button"
              aria-label="Notifications"
              aria-expanded={showPanel}
              onClick={() => setShowPanel((open) => !open)}
              className="relative p-2 hover:bg-amber-700/50 rounded-full transition-all duration-200 hover:scale-110"
            >
              <span className="text-xl leading-none" aria-hidden>
                🔔
              </span>
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 bg-gradient-to-r from-red-500 to-red-600 text-white text-[10px] rounded-full min-w-[1.25rem] h-5 px-1 flex items-center justify-center font-bold shadow-lg">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Bottom Row: Navigation Links (website only) */}
        {!isNative && (
          <div className="flex items-center gap-3 md:gap-4 overflow-x-auto pb-1 hide-scrollbar">
            <Link
              to="/cafes"
              className="flex items-center gap-1.5 hover:text-amber-200 text-sm font-medium whitespace-nowrap py-1 border-b-2 border-transparent hover:border-amber-200 transition-all duration-200"
            >
              <FaStore className="text-xs" />
              <span>Cafes</span>
            </Link>
            <Link
              to="/route"
              className="flex items-center gap-1.5 hover:text-amber-200 text-sm font-medium whitespace-nowrap py-1 border-b-2 border-transparent hover:border-amber-200 transition-all duration-200"
            >
              <FaMapMarkerAlt className="text-xs" />
              <span>Route</span>
            </Link>
            <Link
              to="/orders"
              className="flex items-center gap-1.5 hover:text-amber-200 text-sm font-medium whitespace-nowrap py-1 border-b-2 border-transparent hover:border-amber-200 transition-all duration-200"
            >
              <FaListAlt className="text-xs" />
              <span>Orders</span>
            </Link>
            <Link
              to="/profile"
              className="flex items-center gap-1.5 hover:text-amber-200 text-sm font-medium whitespace-nowrap py-1 border-b-2 border-transparent hover:border-amber-200 transition-all duration-200"
            >
              <FaUser className="text-xs" />
              <span>Profile</span>
            </Link>
            <Link
              to="/settings"
              className="flex items-center gap-1.5 hover:text-amber-200 text-sm font-medium whitespace-nowrap py-1 border-b-2 border-transparent hover:border-amber-200 transition-all duration-200"
            >
              <FaCog className="text-xs" />
              <span>Settings</span>
            </Link>
          </div>
        )}

        {onCafesPage && (
          <div className="mt-3">
            <div className="relative rounded-xl border border-[#d9c5ac] bg-[#fffaf4] px-3 shadow-sm">
              <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8a6042]" />
              <input
                type="text"
                placeholder="Search cafe or address..."
                value={cafeSearch}
                onChange={(e) => updateCafeSearch(e.target.value)}
                className="w-full bg-transparent py-2.5 pl-7 pr-2 text-sm text-[#4a2e1f] placeholder-[#b7a18b] focus:outline-none"
              />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
